// src/pages/estoque/PesqNotaItemEntradaPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { FaSortDown, FaSortUp } from 'react-icons/fa';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useFornecedor } from '../../contexts/pessoas/FornecedorContext';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { getDescricaoTipoMov } from '../../models/estoque/TipoMov.ts';
import { getDescricaoSituacaoNF } from '../../models/vendas/SituacaoNF.ts';
import { buscarItensEntrada } from '../../services/estoque/NotaItemEntradaService';
import Input from '../../components/input/Input';
import InputCheck from '../../components/input/InputCheck';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageFooter from '../../components/geral/PageFooter';
import PageContent from '../../components/geral/PageContent';
import LazyLoading from '../../components/modalForm/LazyLoading';

const PesqNotaItemEntradaPage = () => {
    const { showMessage } = useMessage();
    const { showFornecedorSearch } = useFornecedor();
    const [periodoInicial, setPeriodoInicial] = useState('');
    const [periodoFinal, setPeriodoFinal] = useState('');
    const [horaInicial, setHoraInicial] = useState('00:00');
    const [horaFinal, setHoraFinal] = useState('23:59');
    const [fornecedor, setFornecedor] = useState('');
    const [produtoDescricao, setProdutoDescricao] = useState('');
    const [agruparPorProduto, setAgruparPorProduto] = useState(false);
    const [itens, setItens] = useState([]);
    const [disableBtn, setDisableBtn] = useState(false);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
    const [resultadoVersao, setResultadoVersao] = useState(0);

    const formatDateToInputValue = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const dataAtual = new Date();
        const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
        setPeriodoInicial(formatDateToInputValue(primeiroDiaMes));
        setPeriodoFinal(formatDateToInputValue(dataAtual));
    }, []);

    const buscarItens = async () => {
        try {
            setDisableBtn(true);
            const result = await buscarItensEntrada({
                periodoInicial,
                periodoFinal,
                horaInicial,
                horaFinal,
                fornecedorNome: fornecedor,
                produtoDescricao,
            });
            setItens(result || []);
            setResultadoVersao((versaoAtual) => versaoAtual + 1);
        } catch (error) {
            showMessage(`Erro ao buscar itens das entradas: ${error}`, 'danger');
        } finally {
            setDisableBtn(false);
        }
    };

    useEffect(() => {
        if (periodoInicial && periodoFinal) {
            buscarItens();
        }
        // A consulta inicial deve ocorrer apenas após o preenchimento do período.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodoInicial, periodoFinal]);

    const handleBuscarFornecedor = async () => {
        const fornecedorSel = await showFornecedorSearch();
        setFornecedor(fornecedorSel?.pessoa?.nome || '');
    };

    const getFornecedorId = (item) => item.notaFornecedorId ?? item.fornecedorId ?? '';
    const getFornecedorNome = (item) => item.notaFornecedorNome ?? item.fornecedorNome ?? '';

    const itensExibidos = useMemo(() => {
        const itensBase = !agruparPorProduto
            ? itens
            : Object.values(itens.reduce((acc, item) => {
                const chave = String(item.produtoId || 0);

                if (!acc[chave]) {
                    acc[chave] = {
                        ...item,
                        qtdCom: Number(item.qtdCom || 0),
                        vProd: Number(item.vProd || 0),
                    };
                    return acc;
                }

                acc[chave].qtdCom += Number(item.qtdCom || 0);
                acc[chave].vProd += Number(item.vProd || 0);
                return acc;
            }, {})).map((item) => ({
                ...item,
                vUnCom: Number(item.qtdCom || 0) > 0
                    ? Number(item.vProd || 0) / Number(item.qtdCom || 0)
                    : 0,
            }));

        const { campo, direcao } = ordenacao;
        if (!campo) return itensBase;

        const multiplicador = direcao === 'asc' ? 1 : -1;
        const getValor = (item) => {
            if (campo === 'tipoMovDescricao') return getDescricaoTipoMov(item.notaTipoMov);
            if (campo === 'situacaoDescricao') return getDescricaoSituacaoNF(item.notaSituacaoNF);
            if (campo === 'fornecedorDescricao') return `${getFornecedorId(item)} - ${getFornecedorNome(item)}`;
            if (campo === 'hora') return formatTime(item.notaDataLanc);
            if (campo === 'produtoDescricao') return item.xProd;
            return item?.[campo];
        };

        return [...itensBase].sort((a, b) => {
            const valorA = getValor(a);
            const valorB = getValor(b);

            if (['notaId', 'produtoId', 'qtdCom', 'vUnCom', 'vProd'].includes(campo)) {
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            }
            if (campo === 'notaDataLanc') {
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            }
            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', {
                sensitivity: 'base',
            }) * multiplicador;
        });
    }, [agruparPorProduto, itens, ordenacao]);

    const alterarOrdenacao = (campo) => {
        setOrdenacao((atual) => ({
            campo,
            direcao: atual.campo === campo && atual.direcao === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderOrdenadorColuna = (campo, titulo, style = {}) => {
        const ativa = ordenacao.campo === campo;

        return (
            <button
                type="button"
                onClick={() => alterarOrdenacao(campo)}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    fontWeight: 'bold',
                    color: 'inherit',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: style.textAlign || 'left',
                }}
            >
                {titulo}
                {ativa && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
            </button>
        );
    };

    const quantidadeTotal = itensExibidos.reduce((acc, item) => acc + Number(item.qtdCom || 0), 0);
    const valorTotal = itensExibidos.reduce((acc, item) => acc + Number(item.vProd || 0), 0);

    return (
        <PageContainer>
            <h2 className="page-title">Pesquisar Itens das Entradas</h2>
            <PageHeader>
                <div className="group-div">
                    <InputCheck
                        id="agruparPorProduto"
                        label="Agrupar por Produto"
                        checked={agruparPorProduto}
                        onChange={(e) => setAgruparPorProduto(e.target.checked)}
                    />
                    <Input label="Data Inicial" type="date" size={140} value={periodoInicial} onChange={(e) => setPeriodoInicial(e.target.value)} />
                    <Input label="Hora Início" type="time" size={90} value={horaInicial} onChange={(e) => setHoraInicial(e.target.value)} />
                    <Input label="Data Final" type="date" size={140} value={periodoFinal} onChange={(e) => setPeriodoFinal(e.target.value)} />
                    <Input label="Hora Fim" type="time" size={90} value={horaFinal} onChange={(e) => setHoraFinal(e.target.value)} />
                    <Input
                        label="Produto"
                        value={produtoDescricao}
                        onChange={(e) => setProdutoDescricao(e.target.value)}
                        placeholder="Descrição do produto"
                        size={200}
                    />
                    <Input
                        label="Nome do Fornecedor"
                        placeholder="Digite o nome do fornecedor"
                        onChange={(e) => setFornecedor(e.target.value)}
                        value={fornecedor}
                        btnBuscar={handleBuscarFornecedor}
                        size={220}
                    />
                </div>
                <button
                    style={{ marginRight: '5px' }}
                    type="button"
                    className="btn btn-search"
                    onClick={buscarItens}
                    disabled={disableBtn}
                >
                    Buscar
                </button>
            </PageHeader>

            <LazyLoading
                items={itensExibidos}
                batchSize={1000}
                resetKey={`${resultadoVersao}|${agruparPorProduto}|${ordenacao.campo || ''}|${ordenacao.direcao}`}
            >
                {({ containerRef, handleScroll, visibleItems }) => (
                    <PageContent containerRef={containerRef} onContainerScroll={handleScroll}>
                        <table className="page-content-table">
                    <thead>
                        <tr>
                            {!agruparPorProduto && <th style={{ textAlign: 'right' }}>{renderOrdenadorColuna('notaId', 'Id NF', { textAlign: 'right' })}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('tipoMovDescricao', 'Tipo Mov.')}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('caixaDescricao', 'Caixa')}</th>}
                            {!agruparPorProduto && <th style={{ textAlign: 'center' }}>{renderOrdenadorColuna('notaDataLanc', 'Data', { textAlign: 'center' })}</th>}
                            {!agruparPorProduto && <th style={{ textAlign: 'center' }}>{renderOrdenadorColuna('hora', 'Hora', { textAlign: 'center' })}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('fornecedorDescricao', 'Fornecedor')}</th>}
                            <th style={{ textAlign: 'right' }}>{renderOrdenadorColuna('produtoId', 'Produto', { textAlign: 'right' })}</th>
                            <th>{renderOrdenadorColuna('produtoDescricao', 'Descrição')}</th>
                            <th style={{ textAlign: 'right' }}>{renderOrdenadorColuna('qtdCom', 'Quantidade', { textAlign: 'right' })}</th>
                            <th style={{ textAlign: 'right' }}>{renderOrdenadorColuna('vUnCom', 'Valor Unit.', { textAlign: 'right' })}</th>
                            <th style={{ textAlign: 'right' }}>{renderOrdenadorColuna('vProd', 'Valor Total', { textAlign: 'right' })}</th>
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('situacaoDescricao', 'Sit. NF')}</th>}
                            {!agruparPorProduto && <th>{renderOrdenadorColuna('notaModeloNF', 'Modelo')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map((item) => (
                            <tr key={agruparPorProduto ? `produto-${item.produtoId}` : item.id}>
                                {!agruparPorProduto && <td style={{ textAlign: 'right', minWidth: '70px' }}>{item.notaId}</td>}
                                {!agruparPorProduto && <td style={{ minWidth: '100px' }}>{getDescricaoTipoMov(item.notaTipoMov)}</td>}
                                {!agruparPorProduto && <td style={{ minWidth: '90px' }}>{item.caixaDescricao}</td>}
                                {!agruparPorProduto && <td style={{ textAlign: 'center', minWidth: '100px' }}>{formatDate(item.notaDataLanc)}</td>}
                                {!agruparPorProduto && <td style={{ textAlign: 'center', minWidth: '60px' }}>{formatTime(item.notaDataLanc)}</td>}
                                {!agruparPorProduto && <td style={{ minWidth: '260px' }}>{getFornecedorId(item)} - {getFornecedorNome(item)}</td>}
                                <td style={{ textAlign: 'right', minWidth: '70px' }}>{item.produtoId}</td>
                                <td style={{ minWidth: '250px' }}>{item.xProd}</td>
                                <td style={{ textAlign: 'right', minWidth: '90px' }}>{formatNumeric(item.qtdCom)}</td>
                                <td style={{ textAlign: 'right', minWidth: '100px' }}>{formatCurrency(item.vUnCom)}</td>
                                <td style={{ textAlign: 'right', minWidth: '110px' }}>{formatCurrency(item.vProd)}</td>
                                {!agruparPorProduto && <td style={{ minWidth: '100px' }}>{getDescricaoSituacaoNF(item.notaSituacaoNF)}</td>}
                                {!agruparPorProduto && <td style={{ minWidth: '70px' }}>{item.notaModeloNF}</td>}
                            </tr>
                        ))}
                    </tbody>
                        </table>
                    </PageContent>
                )}
            </LazyLoading>

            <PageFooter>
                <div className="botoes-footer"><strong>Itens: </strong> {itensExibidos.length}</div>
                <div className="totais-footer">
                    <strong>Qtd. Total: </strong> {formatNumeric(quantidadeTotal)}
                    {' | '}
                    <strong>Valor Total: </strong> {formatCurrency(valorTotal)}
                </div>
            </PageFooter>
        </PageContainer>
    );
};

export default PesqNotaItemEntradaPage;
